// Job presentation helpers: active statuses, the name and icon of each job kind, a readable target and phase (queue position and reason, build phase and sub-step) for a job, its progress fraction, ETA and live cost, the steps line and the build pool of each kind.
import {
  ClipboardCheckIcon,
  FileSearchIcon,
  FileTextIcon,
  GaugeIcon,
  MergeIcon,
  PlayIcon,
  RadarIcon,
  ScanSearchIcon,
  ScanTextIcon,
  Trash2Icon,
  Undo2Icon,
  type LucideIcon,
} from "lucide-react";

import type { JobKind, JobProgress, JobStatus, OperationJob } from "@/api/__generated__";
import { STEP_LABELS } from "@/features/operations/labels";
import { formatCurrencyUsd, formatDuration, formatNumber } from "@/lib/format";
import { JOB_KIND_LABELS, JOB_STATUS_LABELS } from "@/lib/labels";

export type JobPhase = NonNullable<JobProgress["phase"]>;

export const ACTIVE_JOB_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>(["queued", "running"]);

export const JOB_KINDS: readonly JobKind[] = [
  "ingest",
  "ingest_resume",
  "ingest_dry_run",
  "source_profile",
  "rollback",
  "delete_version",
  "consolidate_parameters",
  "source_watch",
  "evaluation",
  "capability_extract",
  "assessment_run",
];

export const JOB_KIND_ICONS: Record<JobKind, LucideIcon> = {
  ingest: ScanTextIcon,
  ingest_resume: PlayIcon,
  ingest_dry_run: FileSearchIcon,
  source_profile: ScanSearchIcon,
  rollback: Undo2Icon,
  delete_version: Trash2Icon,
  consolidate_parameters: MergeIcon,
  source_watch: RadarIcon,
  evaluation: GaugeIcon,
  capability_extract: FileTextIcon,
  assessment_run: ClipboardCheckIcon,
};

export const JOB_KIND_NAMES: Record<JobKind, string> = {
  ...JOB_KIND_LABELS,
  source_profile: "Source profile",
};

export const BUILD_KINDS: ReadonlySet<JobKind> = new Set<JobKind>(["ingest", "ingest_resume"]);

export const PHASE_LABELS: Record<JobPhase, string> = {
  queued: "Queued",
  starting: "Starting",
  step: "Running",
  waiting_for_batch: "Waiting for Claude",
  translating: "Translating to English",
  applying: "Applying to the graph",
  cancelling: "Stopping…",
};

export function isActiveJob(job: Pick<OperationJob, "status">): boolean {
  return ACTIVE_JOB_STATUSES.has(job.status);
}

export function isBuildJob(job: Pick<OperationJob, "kind">): boolean {
  return BUILD_KINDS.has(job.kind);
}

export function awaitsAutomaticResume(
  job: Pick<OperationJob, "kind" | "status" | "auto_resumed_by" | "cancel_requested_at">,
): boolean {
  return (
    isBuildJob(job) &&
    job.status === "interrupted" &&
    job.auto_resumed_by === null &&
    job.cancel_requested_at === null
  );
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function jobTarget(job: Pick<OperationJob, "kind" | "params" | "doc_uid" | "run_uid">): string {
  const params = job.params;
  switch (job.kind) {
    case "ingest":
    case "ingest_dry_run":
      return job.doc_uid ?? `${text(params.family_id) ?? "?"}@${text(params.version_label) ?? "?"}`;
    case "ingest_resume":
    case "rollback":
      return job.run_uid ?? text(params.run_uid) ?? "–";
    case "delete_version":
    case "evaluation":
      return job.doc_uid ?? text(params.doc_uid) ?? "–";
    case "source_profile":
      return text(params.sha256)?.slice(0, 12) ?? "–";
    case "consolidate_parameters":
      return params.dry_run === false ? "Apply merges" : "Dry run";
    case "source_watch":
      return "Every registered family";
    case "capability_extract":
      return text(params.capability_doc_uid)?.slice(0, 12) ?? "–";
    case "assessment_run":
      return text(params.assessment_uid) ?? "–";
  }
}

export function queueText(job: Pick<OperationJob, "status" | "queue" | "dispatched_at">): string | null {
  if (job.status !== "queued") {
    return null;
  }
  if (job.queue === null) {
    return job.dispatched_at === null ? null : "Starting";
  }
  return job.queue.reason.startsWith("Starting")
    ? job.queue.reason
    : `Queued · #${formatNumber(job.queue.position)} · ${job.queue.reason}`;
}

export function stepLine(progress: JobProgress | null): string | null {
  if (progress?.steps_total === null || progress === null) {
    return null;
  }
  const done = progress.steps_done ?? 0;
  const current = Math.min(done + 1, progress.steps_total);
  const label =
    progress.step === null ? null : (STEP_LABELS[progress.step] ?? progress.step.replaceAll("_", " "));
  return label === null
    ? `Step ${formatNumber(current)} of ${formatNumber(progress.steps_total)}`
    : `Step ${formatNumber(current)} of ${formatNumber(progress.steps_total)} · ${label}`;
}

export function jobPhase(job: Pick<OperationJob, "status" | "progress" | "queue" | "dispatched_at">): string {
  const queued = queueText(job);
  if (queued !== null) {
    return queued;
  }
  const progress = job.progress;
  if (job.status === "running" && progress !== null) {
    if (progress.phase === "cancelling") {
      return PHASE_LABELS.cancelling;
    }
    if (progress.phase === "waiting_for_batch") {
      return progress.sub_step ?? PHASE_LABELS.waiting_for_batch;
    }
    const step =
      progress.step === null ? null : (STEP_LABELS[progress.step] ?? progress.step.replaceAll("_", " "));
    if (step !== null) {
      return progress.sub_step === null ? step : `${step} · ${progress.sub_step}`;
    }
    if (progress.detail) {
      return progress.detail;
    }
  }
  return JOB_STATUS_LABELS[job.status];
}

export function jobFraction(job: Pick<OperationJob, "status" | "progress">): number | null {
  if (job.status === "succeeded") {
    return 1;
  }
  return job.progress?.fraction ?? null;
}

export function itemsLine(progress: JobProgress | null): string | null {
  if (progress?.items_done === null || progress?.items_done === undefined || progress.items_total === null) {
    return null;
  }
  return `${formatNumber(progress.items_done)} of ${formatNumber(progress.items_total)}`;
}

export function etaText(progress: JobProgress | null): string | null {
  if (progress === null) {
    return null;
  }
  if (progress.phase === "waiting_for_batch") {
    return "Typically under 1 h, at most 24 h";
  }
  if (progress.eta_s === null) {
    return null;
  }
  return progress.eta_s < 60 ? "Less than a minute left" : `About ${formatDuration(progress.eta_s)} left`;
}

export function costText(job: Pick<OperationJob, "progress" | "estimate" | "max_cost_usd">): string | null {
  const spent = job.progress?.cost_usd ?? null;
  if (spent === null) {
    return null;
  }
  const estimate = job.estimate?.cost_usd ?? null;
  const parts = [`Spent ${formatCurrencyUsd(spent)}`];
  if (estimate !== null) {
    parts.push(`of ≈ ${formatCurrencyUsd(estimate)}`);
  }
  if (job.max_cost_usd !== null) {
    parts.push(`(cap ${formatCurrencyUsd(job.max_cost_usd)})`);
  }
  return parts.join(" ");
}

export function jobDurationSeconds(
  job: Pick<OperationJob, "started_at" | "finished_at">,
  now: number = Date.now(),
): number | null {
  if (job.started_at === null) {
    return null;
  }
  const end = job.finished_at === null ? now : new Date(job.finished_at).getTime();
  return Math.max(0, (end - new Date(job.started_at).getTime()) / 1000);
}
